import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Megaphone, Send, Eye, X, RefreshCw, Mail } from "lucide-react";

const AUDIENCES = [
  { key: "all",     label: "All users",          hint: "Every account (excludes unsubscribed)" },
  { key: "premium", label: "Premium only",       hint: "Paying subscribers" },
  { key: "free",    label: "Free + trial only",  hint: "Everyone not paying" },
];

const STATUS_PILL = {
  sending: "bg-mm-gold/25 text-mm-navy",
  done:    "bg-emerald-100 text-emerald-700",
  failed:  "bg-red-100 text-red-700",
};

const AdminBroadcasts = () => {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("all");
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null); // {recipient_count, subject, html}
  const [confirmSend, setConfirmSend] = useState(false);
  const [sending, setSending] = useState(false);
  const [broadcasts, setBroadcasts] = useState([]);
  const [loadingLog, setLoadingLog] = useState(false);

  const subjectCount = subject.length;
  const bodyCount = body.length;
  const canSubmit = useMemo(
    () => subject.trim().length >= 3 && body.trim().length >= 5 && !sending,
    [subject, body, sending]
  );

  const loadLog = async () => {
    setLoadingLog(true);
    try {
      const r = await api.get("/admin/broadcasts");
      setBroadcasts(r.data.broadcasts || []);
    } catch (err) {
      toast.error("Could not load broadcast log");
    } finally {
      setLoadingLog(false);
    }
  };

  useEffect(() => { loadLog(); }, []);

  const openPreview = async () => {
    if (!canSubmit) {
      toast.error("Subject (3+ chars) and body (5+ chars) are required.");
      return;
    }
    setPreviewing(true);
    try {
      const r = await api.post("/admin/broadcasts/preview", { subject, body, audience });
      setPreview(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not render preview");
    } finally {
      setPreviewing(false);
    }
  };

  const askToSend = async () => {
    // Re-fetch recipient count so the confirmation reflects the live audience.
    try {
      const r = await api.post("/admin/broadcasts/preview", { subject, body, audience });
      setPreview(r.data);
      setConfirmSend(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not check recipient count");
    }
  };

  const doSend = async () => {
    setSending(true);
    try {
      const r = await api.post("/admin/broadcasts/send", { subject, body, audience });
      toast.success(`Broadcast queued — sending to ${r.data.recipient_count} recipient${r.data.recipient_count === 1 ? "" : "s"}.`);
      setSubject("");
      setBody("");
      setAudience("all");
      setConfirmSend(false);
      setPreview(null);
      // Give the background sender a moment, then refresh
      setTimeout(loadLog, 400);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-8" data-testid="admin-broadcasts-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-heading text-xl font-bold text-mm-navy flex items-center gap-2">
            <Megaphone size={20} strokeWidth={2.5} /> Broadcast email
          </h2>
          <p className="text-sm text-mm-navy/60">
            Send a one-time announcement to your users via Resend. Includes CAN-SPAM unsubscribe link automatically.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Composer */}
        <div className="lg:col-span-2 bg-white border border-mm-navy/10 rounded-2xl p-6">
          <label className="text-xs uppercase tracking-widest font-bold text-mm-steel">Subject line</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={200}
            placeholder="Important — Bookmark Your New Mind Map Link"
            className="mt-1.5 w-full bg-white border-2 border-mm-navy/15 rounded-lg px-4 py-3 focus:outline-none focus:border-mm-steel focus:ring-2 focus:ring-mm-steel/30"
            data-testid="broadcast-subject-input"
          />
          <div className="text-[10px] text-mm-navy/45 text-right mt-1">{subjectCount}/200</div>

          <label className="text-xs uppercase tracking-widest font-bold text-mm-steel mt-3 block">Body</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={20000}
            rows={14}
            placeholder={"Hi {{first_name}},\n\nWrite your message here. Use **two asterisks** for bold, and bare https://… URLs become clickable. Leave a blank line for a new paragraph."}
            className="mt-1.5 w-full bg-white border-2 border-mm-navy/15 rounded-lg px-4 py-3 focus:outline-none focus:border-mm-steel focus:ring-2 focus:ring-mm-steel/30 font-mono text-sm leading-relaxed"
            data-testid="broadcast-body-input"
          />
          <div className="flex justify-between mt-1 text-[10px] text-mm-navy/45">
            <span>
              Tokens: <code className="font-mono">&#123;&#123;first_name&#125;&#125;</code>, <code className="font-mono">**bold**</code>, bare URLs auto-link
            </span>
            <span>{bodyCount}/20,000</span>
          </div>

          <label className="text-xs uppercase tracking-widest font-bold text-mm-steel mt-5 block">Audience</label>
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="broadcast-audience-group">
            {AUDIENCES.map((a) => (
              <button
                key={a.key}
                type="button"
                onClick={() => setAudience(a.key)}
                className={`text-left p-3 rounded-xl border-2 transition-all ${
                  audience === a.key
                    ? "border-mm-gold bg-mm-gold/10"
                    : "border-mm-navy/10 hover:border-mm-navy/30 bg-white"
                }`}
                data-testid={`broadcast-audience-${a.key}`}
              >
                <div className="text-sm font-bold text-mm-navy">{a.label}</div>
                <div className="text-[11px] text-mm-navy/55 mt-0.5">{a.hint}</div>
              </button>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap gap-2 justify-end">
            <button
              type="button"
              onClick={openPreview}
              disabled={!canSubmit || previewing}
              className="btn-trail btn-outline-navy text-sm"
              data-testid="broadcast-preview-btn"
            >
              <Eye size={14} strokeWidth={2.5} /> {previewing ? "Rendering…" : "Preview"}
            </button>
            <button
              type="button"
              onClick={askToSend}
              disabled={!canSubmit}
              className="btn-trail btn-gold text-sm"
              data-testid="broadcast-send-btn"
            >
              <Send size={14} strokeWidth={2.5} /> Send broadcast
            </button>
          </div>
        </div>

        {/* CAN-SPAM tip card */}
        <div className="bg-mm-parchment border-2 border-mm-navy/10 rounded-2xl p-5 h-fit" data-testid="broadcast-canspam-card">
          <div className="text-xs uppercase tracking-widest font-bold text-mm-steel flex items-center gap-1.5">
            <Mail size={12} strokeWidth={2.5} /> CAN-SPAM ready
          </div>
          <ul className="mt-3 space-y-2 text-sm text-mm-navy/80">
            <li>• Unsubscribe link is added to every email automatically.</li>
            <li>• Recipients who previously unsubscribed are excluded.</li>
            <li>• Every send is logged with date, subject, and recipient count.</li>
            <li>• Sender identity comes from your verified Resend domain.</li>
          </ul>
        </div>
      </div>

      {/* Log */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-lg font-bold text-mm-navy">Sent log</h3>
          <button
            type="button"
            onClick={loadLog}
            disabled={loadingLog}
            className="inline-flex items-center gap-1.5 text-xs font-bold text-mm-steel hover:text-mm-navy"
            data-testid="broadcast-log-refresh-btn"
          >
            <RefreshCw size={12} strokeWidth={2.5} className={loadingLog ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
        <div className="overflow-x-auto bg-white border border-mm-navy/10 rounded-2xl" data-testid="broadcast-log-table">
          <table className="w-full text-sm">
            <thead className="bg-mm-parchment text-mm-navy">
              <tr className="text-left">
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold text-mm-steel">Sent</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold text-mm-steel">Subject</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold text-mm-steel">Audience</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold text-mm-steel">Recipients</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-bold text-mm-steel">Status</th>
              </tr>
            </thead>
            <tbody>
              {broadcasts.map((b) => (
                <tr key={b.id} className="border-t border-mm-navy/10 align-top" data-testid={`broadcast-log-row-${b.id}`}>
                  <td className="px-4 py-3 text-xs text-mm-navy/70 whitespace-nowrap">{b.created_at?.slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-3">
                    <div className="font-bold text-mm-navy">{b.subject}</div>
                    {b.body_preview && (
                      <div className="text-xs text-mm-navy/55 mt-1 line-clamp-2 whitespace-pre-line">{b.body_preview}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 rounded-full bg-mm-steel/15 text-mm-navy text-xs font-bold uppercase tracking-widest">{b.audience}</span>
                  </td>
                  <td className="px-4 py-3 text-mm-navy/80">
                    <div className="font-bold">{b.sent_count ?? 0} <span className="text-mm-navy/40 font-normal">/ {b.recipient_count ?? 0}</span></div>
                    {b.failed_count > 0 && <div className="text-[11px] text-red-600">{b.failed_count} failed</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-widest ${STATUS_PILL[b.status] || "bg-mm-navy/10 text-mm-navy"}`}>{b.status || "unknown"}</span>
                  </td>
                </tr>
              ))}
              {!broadcasts.length && !loadingLog && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-mm-navy/55">No broadcasts sent yet.</td></tr>
              )}
              {loadingLog && !broadcasts.length && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-mm-navy/55">Loading…</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Preview modal */}
      {preview && !confirmSend && (
        <div className="fixed inset-0 z-50 bg-mm-navy/60 backdrop-blur-sm flex items-center justify-center px-4 py-8" data-testid="broadcast-preview-modal" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-full flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-mm-navy/10 flex items-start justify-between gap-3 bg-mm-parchment">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-mm-steel">Email preview</div>
                <div className="font-heading font-black text-mm-navy mt-0.5">{preview.subject}</div>
                <div className="text-xs text-mm-navy/60 mt-1">
                  Will send to <strong data-testid="broadcast-preview-recipient-count">{preview.recipient_count}</strong> {preview.audience === "all" ? "user" : `${preview.audience} user`}{preview.recipient_count === 1 ? "" : "s"}
                </div>
              </div>
              <button onClick={() => setPreview(null)} aria-label="Close preview" className="p-1.5 rounded-lg text-mm-navy/50 hover:text-mm-navy hover:bg-mm-navy/5"><X size={18} strokeWidth={2.5} /></button>
            </div>
            <iframe
              title="Broadcast preview"
              srcDoc={preview.html}
              className="flex-1 w-full min-h-[60vh] bg-white"
              data-testid="broadcast-preview-iframe"
            />
            <div className="px-5 py-3 border-t border-mm-navy/10 bg-white flex flex-wrap justify-end gap-2">
              <button onClick={() => setPreview(null)} className="btn-trail btn-outline-navy text-sm">Close</button>
              <button onClick={() => setConfirmSend(true)} className="btn-trail btn-gold text-sm" data-testid="broadcast-preview-send-btn">
                <Send size={14} strokeWidth={2.5} /> Send to {preview.recipient_count}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation dialog */}
      {confirmSend && preview && (
        <div className="fixed inset-0 z-50 bg-mm-navy/70 backdrop-blur-sm flex items-center justify-center px-4" data-testid="broadcast-confirm-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 border-2 border-mm-gold/40 fade-up">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-mm-gold/20 text-mm-navy flex items-center justify-center shrink-0">
                <Send size={20} strokeWidth={2.5} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest font-bold text-mm-steel">Confirm broadcast</div>
                <h3 className="font-heading text-xl font-black text-mm-navy mt-1">
                  You are about to send this email to <span data-testid="broadcast-confirm-count">{preview.recipient_count}</span> user{preview.recipient_count === 1 ? "" : "s"}. Are you sure?
                </h3>
                <p className="text-sm text-mm-navy/70 mt-2">
                  Audience: <strong>{preview.audience}</strong>. This action sends real email via Resend and cannot be undone.
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setConfirmSend(false)} disabled={sending} className="btn-trail btn-outline-navy text-sm" data-testid="broadcast-confirm-cancel-btn">Cancel</button>
              <button onClick={doSend} disabled={sending} className="btn-trail btn-gold text-sm" data-testid="broadcast-confirm-send-btn">
                {sending ? "Sending…" : <>Yes, send now <Send size={14} strokeWidth={2.5} /></>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBroadcasts;
